import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CheckoutPatientAddress,
  CheckoutPatientAttributes,
  CheckoutPatientData,
  CheckoutPaymentResponse
} from 'components/modules/patientArea/products/types'
import { MAIS_LIB_VARIANTS } from 'constants/products/menPower'
import { ShipmentData } from 'redux/slices/packages-slice'
import { createPatientData } from 'redux/thunks/create-patient-data'
import { createSpreeAddressId } from 'redux/thunks/create-spree-address-id'
import { fetchShipmentData } from 'redux/thunks/fetch-shipment-data'
import nestApiService from 'services/api/nest-api-service'
import { RecordOfAny } from 'types'
import { getOpenJwtToken } from 'utils/getOpenJwtToken'

export interface PhytoVariantData {
  price: number
  variantId: string | number
  label: string
  description: string
  imgSrc: string
  descriptionSrc: string
  amount: number
}

export interface menPowerCouponResponseData {
  orderId: number
  couponSuccess: boolean
  total: number
}

export interface CheckoutMenPowerState {
  isLoading: boolean
  activeVariantId: number | string | null
  deliveryType: 'PAC' | 'SEDEX' | ''
  spreeAddressId: number | null
  patientAttributes: CheckoutPatientAttributes | null
  patientAddress: CheckoutPatientAddress | null
  patientData: CheckoutPatientData | null
  shipmentData: ShipmentData[]
  paymentResponse: CheckoutPaymentResponse | null
  orderVariants: PhytoVariantData[]
  menPowerCouponResponseData: menPowerCouponResponseData | null
}

const initialState: CheckoutMenPowerState = {
  isLoading: false,
  activeVariantId: null,
  deliveryType: 'SEDEX',
  spreeAddressId: null,
  patientAttributes: null,
  patientAddress: null,
  patientData: null,
  shipmentData: [],
  paymentResponse: null,
  orderVariants: MAIS_LIB_VARIANTS,
  menPowerCouponResponseData: null
}

const checkoutMenPowerSlice = createSlice({
  name: 'checkout-men-power',
  initialState,
  reducers: {
    resetCheckoutMenPowerState: (state) => {
      state = initialState
      return state
    },
    setActiveVariantId: (state, { payload }: PayloadAction<number | string | null>) => {
      state.activeVariantId = payload
    },
    setDeliveryType: (state, { payload }: PayloadAction<'PAC' | 'SEDEX' | ''>) => {
      state.deliveryType = payload
    },
    setPatientAttributes: (state, { payload }: PayloadAction<CheckoutPatientAttributes | null>) => {
      state.patientAttributes = payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchShipmentData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchShipmentData.fulfilled, (state, action) => {
        state.isLoading = false
        state.shipmentData = action.payload
      })
      .addCase(fetchShipmentData.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(createSpreeAddressId.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createSpreeAddressId.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.spreeAddressId = payload
      })
      .addCase(createSpreeAddressId.rejected, (state) => {
        state.isLoading = false
      })

      .addCase(createPatientData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createPatientData.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.patientData = payload
      })
      .addCase(createPatientData.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(applyMenPowerCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(applyMenPowerCoupon.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.menPowerCouponResponseData = payload
      })
      .addCase(applyMenPowerCoupon.rejected, (state) => {
        state.isLoading = false
        state.menPowerCouponResponseData = null
      })
      .addCase(menPowerCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(menPowerCheckout.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(menPowerCheckout.rejected, (state) => {
        state.isLoading = false
      })
})

export const { resetCheckoutMenPowerState, setActiveVariantId, setDeliveryType, setPatientAttributes } =
  checkoutMenPowerSlice.actions

interface MenPowerCheckoutParams {
  orderData: RecordOfAny
  coupon?: boolean | null
  order_id?: number | null
}

export const menPowerCheckout = createAsyncThunk(
  'men-power-checkout',
  async ({ orderData, coupon, order_id }: MenPowerCheckoutParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()
      let paymentResponse

      if (coupon) {
        paymentResponse = await nestApiService.apiInstance.post(
          'phytotherapy/checkout/payment',
          { ...orderData, order_id: order_id },
          {
            headers: {
              Authorization: token
            }
          }
        )
      } else {
        const orderResponse = await nestApiService.apiInstance.post<CheckoutPaymentResponse>(
          'phytotherapy/checkout/order',
          orderData,
          {
            headers: {
              Authorization: token
            }
          }
        )

        paymentResponse = await nestApiService.apiInstance.post(
          'phytotherapy/checkout/payment',
          { ...orderData, order_id: orderResponse.data.orderId },
          {
            headers: {
              Authorization: token
            }
          }
        )
      }

      return paymentResponse.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface ApplyMenPowerCouponParams {
  coupon_code: string
  variant_id: string | number | null
  spree_address_id: number | null
  patient_id?: number | null
  shipment_total: number
  smart_fit_price?: number | null
}

export const applyMenPowerCoupon = createAsyncThunk(
  'apply-men-power-coupon',
  async (params: ApplyMenPowerCouponParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post('phytotherapy/checkout/coupon', params, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
export default checkoutMenPowerSlice.reducer

export type CheckoutMenPowerStep = 'info' | 'address' | 'pick-variant' | 'payment' | 'confirm'

export const MEN_POWER_CHECKOUT_PATHNAME = '/men-power-checkout/[step]'
export const MEN_POWER_CHECKOUT_INITIAL_STEP = `/men-power-checkout/info`
export const GLP1_CHECKOUT_INITIAL_STEP = `/men-power-checkout/pick-variant`
